import type { Tone } from './lib/tone';

/**
 * Status-Mapping für alle Apps (README „Status“). Ein neuer Status wird ZUERST hier
 * eingetragen. Unbekannte Schlüssel zeigt `Status` neutral mit dem Rohwert, damit
 * Lücken auffallen.
 *
 * Abweichung vom Zip (design-system/components/src/index.tsx), entschieden am
 * 22.10.2026 (docs/it/plan-design-system.md, E2/E3):
 * - PM hat seit dem Status-Kollaps sieben Status (elbklar_core.models.project.
 *   ProjectStatus); bis zum 27.10.2026 waren es fünf, `in_umsetzung` und
 *   `abgeschlossen` kamen mit der Einsatzplanung (Ebene 5) dazu. Die 13 alten
 *   PM-Phasen der Referenz gibt es nicht mehr.
 * - `angebot_raus` heißt wie im CRM „Angebot gesendet“.
 *
 * Ergänzt am 22.10.2026 (Mobil): Anfragen, Partner und Aufgaben aus dem CRM.
 * Anfragen tragen die Werte des Backends (GROSS, eindeutig). Partner und
 * Aufgaben haben im Backend allgemeine Werte (`active`, `open`), darum hier mit
 * Präfix: `<Status status={`partner_${p.status}`} />`, `task_${t.status}`.
 *
 * Ebenso Angebote (`quote_${q.status}`, elbklar_core.models.quote.QuoteStatus)
 * und Einsätze (`mission_${m.status}`, elbklar_core.models.mission.
 * MissionStatus). `quote_sent` gab es schon als Kundenstatus „Angebot
 * gesendet“; ein gesendetes Angebot heißt genauso und hat denselben Ton.
 *
 * Gegenprüfung Mobil (22.10.2026): Priorität einer Aufgabe
 * (`priority_${t.priority}`) und Dringlichkeit einer Anfrage bzw. eines
 * Projekts (`dringlichkeit_${d}`). Keine Datensatz-Status, aber Wort und Ton
 * gehören an eine Stelle; CRM und PM hatten sie mehrfach, mit
 * verschiedenen Tönen.
 *
 * Auftrag 010, D2 (28.10.2026): das Radar der Finanzen mit Präfixen
 * (`radar_`, `akteur_`, `lauf_`, `quelle_`), weil Rohwerte wie `neu`,
 * `aktiv` und `abgelehnt` schon vergeben sind.
 */
export const STATUS = {
  // CRM: Leads
  lead_open: { label: 'Ausstehend', tone: 'neutral' },
  lead_contacted: { label: 'Kontaktiert', tone: 'info' },
  lead_ask_on_site: { label: 'Vor Ort nachfragen', tone: 'warning' },
  lead_lost: { label: 'Verloren', tone: 'danger' },
  // CRM: Kunden
  viewing_scheduled: { label: 'Besichtigung', tone: 'info' },
  quote_to_send: { label: 'Angebot senden', tone: 'warning' },
  ask_on_site: { label: 'Vor Ort nachfragen', tone: 'warning' },
  quote_sent: { label: 'Angebot gesendet', tone: 'highlight' },
  won: { label: 'Gewonnen', tone: 'success' },
  lost: { label: 'Verloren', tone: 'danger' },
  interested_later: { label: 'Später', tone: 'neutral' },
  // PM: Projekte
  erfasst: { label: 'Erfasst', tone: 'neutral' },
  besichtigt: { label: 'Besichtigt', tone: 'info' },
  angebot_raus: { label: 'Angebot gesendet', tone: 'highlight' },
  beauftragt: { label: 'Beauftragt', tone: 'success' },
  // Einsatzplanung Ebene 5 (27.10.2026). „Beauftragt“ heißt seitdem „Auftrag
  // da, noch nicht geplant“ und bleibt success; was gerade läuft, steht nach
  // der Ton-Regel in highlight, der fertige Vorgang in success.
  in_umsetzung: { label: 'In Umsetzung', tone: 'highlight' },
  abgeschlossen: { label: 'Abgeschlossen', tone: 'success' },
  abgelehnt: { label: 'Abgelehnt', tone: 'danger' },
  // CRM: Anfragen (Pipeline, elbklar_core.models.anfrage.AnfrageStatus)
  NEU: { label: 'Neu', tone: 'neutral' },
  IN_BEARBEITUNG: { label: 'In Bearbeitung', tone: 'highlight' },
  IN_PLANUNG: { label: 'In Planung', tone: 'info' },
  GEWONNEN: { label: 'Gewonnen', tone: 'success' },
  VERLOREN: { label: 'Verloren', tone: 'danger' },
  // CRM: Partner
  partner_prospect: { label: 'Interessent', tone: 'info' },
  partner_active: { label: 'Aktiv', tone: 'highlight' },
  partner_inactive: { label: 'Inaktiv', tone: 'neutral' },
  // CRM: Aufgaben
  task_open: { label: 'Offen', tone: 'neutral' },
  task_in_progress: { label: 'In Bearbeitung', tone: 'highlight' },
  task_completed: { label: 'Erledigt', tone: 'success' },
  task_cancelled: { label: 'Abgebrochen', tone: 'neutral' },
  // Wartet auf etwas, das jemand lösen muss: nach der Ton-Regel „wir müssen handeln“.
  task_blocked: { label: 'Blockiert', tone: 'warning' },
  // CRM: Priorität einer Aufgabe (TaskPriority). Was auffallen soll, hat
  // einen Ton; das Höchste steht wie „Überfällig“ in danger.
  priority_low: { label: 'Niedrig', tone: 'neutral' },
  priority_medium: { label: 'Mittel', tone: 'neutral' },
  priority_high: { label: 'Hoch', tone: 'warning' },
  priority_urgent: { label: 'Dringend', tone: 'danger' },
  // CRM und PM: Dringlichkeit einer Anfrage, im PM die des Projekts.
  // „Langfristig“ ruht wie „Später“ (neutral), es ist keine laufende Planung.
  dringlichkeit_langfristig: { label: 'Langfristig', tone: 'neutral' },
  dringlichkeit_normal: { label: 'Normal', tone: 'neutral' },
  dringlichkeit_schnell: { label: 'Schnell', tone: 'warning' },
  dringlichkeit_dringend: { label: 'Dringend', tone: 'warning' },
  dringlichkeit_sofort: { label: 'Sofort', tone: 'danger' },
  // CRM: Angebote (Kunden-Detail). `quote_sent` steht oben bei den Kunden.
  quote_draft: { label: 'Entwurf', tone: 'neutral' },
  quote_viewed: { label: 'Angesehen', tone: 'highlight' },
  quote_accepted: { label: 'Angenommen', tone: 'success' },
  quote_rejected: { label: 'Abgelehnt', tone: 'danger' },
  quote_expired: { label: 'Abgelaufen', tone: 'neutral' },
  quote_cancelled: { label: 'Zurückgezogen', tone: 'neutral' },
  // CRM: Einsätze (Kunden-Detail). Nach dem Flug laufen die Fotos durch die
  // Auswertung: Was gerade läuft, steht in highlight, was auf den nächsten
  // Schritt wartet, neutral.
  mission_created: { label: 'Angelegt', tone: 'neutral' },
  mission_uploading: { label: 'Fotos werden hochgeladen', tone: 'highlight' },
  mission_uploaded: { label: 'Fotos hochgeladen', tone: 'neutral' },
  mission_validating: { label: 'Fotos werden geprüft', tone: 'highlight' },
  mission_validated: { label: 'Fotos geprüft', tone: 'neutral' },
  mission_analyzing: { label: 'Wird ausgewertet', tone: 'highlight' },
  mission_analyzed: { label: 'Ausgewertet', tone: 'neutral' },
  mission_clustering: { label: 'Zonen werden gebildet', tone: 'highlight' },
  mission_zoned: { label: 'Zonen gebildet', tone: 'neutral' },
  mission_planning: { label: 'Plan wird erstellt', tone: 'highlight' },
  mission_complete: { label: 'Abgeschlossen', tone: 'success' },
  mission_error: { label: 'Fehlgeschlagen', tone: 'danger' },
  mission_cancelled: { label: 'Abgebrochen', tone: 'neutral' },
  // Finanzen: Radar (Auftrag 010, D2; Tabelle „Status-Erweiterungen“ im Plan).
  // Einträge im Radar (`radar_${p.status}`, finance-api schemas/radar.py).
  // Was ruht oder wartet, steht neutral, auch „Geprüft“: Der Eintrag ist
  // gesichtet, aber niemand arbeitet daran.
  radar_neu: { label: 'Neu', tone: 'neutral' },
  radar_geprueft: { label: 'Geprüft', tone: 'neutral' },
  radar_beobachten: { label: 'Wird beobachtet', tone: 'highlight' },
  radar_in_bearbeitung: { label: 'In Bearbeitung', tone: 'highlight' },
  radar_beantragt: { label: 'Beantragt', tone: 'highlight' },
  radar_bewilligt: { label: 'Bewilligt', tone: 'success' },
  radar_abgelehnt: { label: 'Abgelehnt', tone: 'danger' },
  radar_verworfen: { label: 'Verworfen', tone: 'neutral' },
  radar_abgelaufen: { label: 'Abgelaufen', tone: 'neutral' },
  // Akteure im Radar: Ein Vorschlag der Suche will von einem Menschen geprüft werden.
  akteur_vorgeschlagen: { label: 'Zu prüfen', tone: 'warning' },
  akteur_aktiv: { label: 'Aktiv', tone: 'highlight' },
  akteur_inaktiv: { label: 'Inaktiv', tone: 'neutral' },
  // Läufe des Radars (Prüfung und Suche).
  lauf_laufend: { label: 'Läuft', tone: 'highlight' },
  lauf_fertig: { label: 'Abgeschlossen', tone: 'success' },
  lauf_fehler: { label: 'Fehlgeschlagen', tone: 'danger' },
  // Zustand der Quelle eines Eintrags (`quelle_${p.source_state}`). „Lesbar“
  // läuft: Das Radar überwacht die Quelle selbst. Alles, was einen Menschen
  // braucht (händisch prüfen, Adresse prüfen), steht in warning; welcher Fall
  // es ist, sagen Wort und Icon (die App gibt das Icon über `Status icon`).
  quelle_ungeprueft: { label: 'Ungeprüft', tone: 'neutral' },
  quelle_lesbar: { label: 'Lesbar', tone: 'highlight' },
  quelle_umgezogen: { label: 'Umgezogen', tone: 'warning' },
  quelle_unsicher: { label: 'Unsicher', tone: 'warning' },
  quelle_js: { label: 'Nur im Browser lesbar', tone: 'warning' },
  quelle_gesperrt: { label: 'Gesperrt', tone: 'warning' },
  quelle_quelle_tot: { label: 'Quelle tot', tone: 'warning' },
  quelle_nicht_lesbar: { label: 'Nicht lesbar', tone: 'warning' },
} as const satisfies Record<string, { label: string; tone: Tone }>;

export type StatusKey = keyof typeof STATUS;

export function statusInfo(key: string): { label: string; tone: Tone } {
  return (STATUS as Record<string, { label: string; tone: Tone }>)[key] ?? { label: key, tone: 'neutral' };
}
